import type { CodeString } from '@/consts/codes';
import type { MenuItem } from '@/consts/navigation';
import type { TranslationKey } from '@/i18n/i18n';

interface RouteInfo {
  route?: string;
  code?: CodeString;
  label: TranslationKey;
  parent?: TranslationKey;
  parentChain?: TranslationKey[];
  isRedirectOnly?: boolean;
}

export const findMatchingRoute = (fullPath: string, routes: RouteInfo[]): RouteInfo | undefined => {
  // Exact match (handles both clean paths and paths with query strings)
  let match = routes.find((r) => r.route === fullPath);

  // Query-stripped path, shared by the parameterized and ancestor passes below.
  const pathname = fullPath.split('?')[0] ?? fullPath;

  // Try matching without query strings for parameterized routes
  match ??= routes.find((r) => {
    if (!r.route) return false;
    const routePath = r.route.split('?')[0] ?? r.route;
    const pattern = routePath.replace(/:\w+/g, '[^/]+');
    return new RegExp(`^${pattern}$`).test(pathname);
  });

  // Fallback: nearest (longest) ancestor route. A detail page whose URL has no
  // menu entry of its own — e.g. the catalogue browser at /pur/catalogue/:id,
  // which the bare /pur/catalogue index redirects into — inherits the breadcrumb
  // of its closest registered ancestor (/pur/catalogue). Only consulted after the
  // exact + parameterized passes fail, so it can never override a more specific
  // match; it only supplies a breadcrumb where there would otherwise be none.
  match ??= routes.reduce<RouteInfo | undefined>((best, r) => {
    if (!r.route || r.route.includes(':')) return best;
    const routePath = r.route.split('?')[0] ?? r.route;
    if (!pathname.startsWith(`${routePath}/`)) return best;
    const bestPath = best?.route?.split('?')[0] ?? '';
    return routePath.length > bestPath.length ? r : best;
  }, undefined);

  return match;
};

const buildRoutesRecursive = (item: MenuItem, parentChain: TranslationKey[] = []): RouteInfo[] => {
  const routes: RouteInfo[] = [];
  const currentChain = [...parentChain, item.text];

  if (item.to) {
    const hasChildren = Boolean(item.children?.length);
    routes.push({
      route: item.to,
      label: item.text,
      code: item.code,
      parent: parentChain[parentChain.length - 1],
      parentChain: parentChain.length > 0 ? parentChain : undefined,
      isRedirectOnly: hasChildren
    });
  }

  if (item.children) {
    for (const child of item.children) {
      routes.push(...buildRoutesRecursive(child, currentChain));
    }
  }

  return routes;
};

export const buildRouteMapping = (menu: MenuItem[]): RouteInfo[] => {
  return menu.flatMap<RouteInfo>((section) => {
    // Group headers (OPERATIONS, TECHNICAL, etc.) are not navigation levels —
    // skip them in the parent chain and process their children directly.
    if (section.isGroup && section.children) {
      return section.children.flatMap((child) => {
        if (child.children) {
          return child.children.flatMap((grandchild) => buildRoutesRecursive(grandchild, [child.text]));
        }
        return child.to ? [{ label: child.text, route: child.to }] : [];
      });
    }

    if (section.children) {
      return section.children.flatMap((child) => buildRoutesRecursive(child, [section.text]));
    }

    return section.to ? [{ label: section.text, route: section.to }] : [];
  });
};
